import { useEffect, useState } from "react";
import { tr } from "../../i18n/tr";
import { Wifi, WifiOff, RefreshCw, DownloadCloud, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useOfflineStatus, prepareSessionOffline, getPreparedInfo } from "../../lib/offline/offlineApi";

/** Barre d'état hors ligne : réseau, opérations en attente, préparation d'une session. */
export function OfflineBar({ api, sessionId, role, enabled = true }) {
  const { online, pending, syncing, lastSync, lastError, sync } = useOfflineStatus();
  const [prepared, setPrepared] = useState(null);
  const [prep, setPrep] = useState(null);

  useEffect(() => { if (sessionId) getPreparedInfo(sessionId).then(setPrepared).catch(() => {}); }, [sessionId, prep]);

  const prepare = async () => {
    if (!online) { toast.error(tr("lms.connectez_vous_au_reseau_546d", "Connectez-vous au réseau pour préparer le mode hors ligne")); return; }
    setPrep({ label: tr("lms.demarrage_6ade", "Démarrage…"), done: 0, total: 1 });
    try {
      const info = await prepareSessionOffline(api, sessionId, role, setPrep);
      toast.success(`Session disponible hors ligne (${info.modules} modules${info.scormFiles ? `, ${info.scormFiles} fichiers SCORM` : ""})`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setPrep(null);
  };

  if (!enabled) return null;
  const pct = prep && prep.total ? Math.round((prep.done / prep.total) * 100) : 0;
  return (
    <div className={"flex flex-wrap items-center gap-2 px-3 py-1.5 rounded-xl border text-[11px] " + (online ? "bg-[#14101c] border-white/10" : "bg-amber-500/10 border-amber-500/30")} data-testid="offline-bar">
      <span className={"inline-flex items-center gap-1 font-bold " + (online ? "text-emerald-400" : "text-amber-300")} data-testid="offline-status">
        {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />} {online ? "En ligne" : "Hors ligne — vos actions seront synchronisées"}
      </span>
      {pending > 0 && (
        <button onClick={sync} disabled={!online || syncing} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 font-bold disabled:opacity-60" data-testid="offline-pending">
          {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} {pending} en attente{online ? " — synchroniser" : ""}
        </button>
      )}
      {lastError && <span className="text-red-400" data-testid="offline-error">{lastError}</span>}
      {!pending && lastSync && <span className="text-slate-500">Synchro {new Date(lastSync).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>}
      {sessionId && (
        <span className="ml-auto inline-flex items-center gap-2">
          {prepared && !prep && <span className="inline-flex items-center gap-1 text-emerald-400" data-testid="offline-prepared"><CheckCircle2 className="w-3.5 h-3.5" /> Disponible hors ligne ({new Date(prepared.at).toLocaleDateString("fr-FR")})</span>}
          {prep ? (
            <span className="inline-flex items-center gap-2 text-slate-300" data-testid="offline-prep-progress"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {prep.label} {pct}%</span>
          ) : (
            <button onClick={prepare} disabled={!online} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold text-[#14101c] disabled:opacity-50" style={{ background: "var(--brand-primary, #f7c935)" }} data-testid="offline-prepare-btn">
              <DownloadCloud className="w-3.5 h-3.5" /> {prepared ? "Mettre à jour hors ligne" : "Préparer hors ligne"}
            </button>
          )}
        </span>
      )}
    </div>
  );
}

export default OfflineBar;
